import {MenuItem, Select, TableCell, TableRow, TextField, Grid, IconButton, Stack} from "@mui/material";
import {MediaDownloadToken, UpdateButton} from "./tools";
import {FormattedMessage, defineMessage, defineMessages} from "react-intl";
import {IvrActionType, UpdateConnectionPrompt} from "@/api";
import {DeepPartial, removeFileExtension} from "@/tools";
import TargetSelector, {Target} from "@/components/TargetSelector";
import {FormattedButton, FormattedTooltip, ErrorWithContext, MultipleLineError} from "@dvrt/graphql-widgets";
import ThemedFontAwesomeIcon from "@/components/ThemedFontAwesomeIcon";
import DomainMediaSelector from "@/components/DomainMediaSelector";

type BaseProps = {
    /** Whether the component is disabled or not */
    disabled: boolean;
};

type ButtonTypeSelectorProps = {
    currentType: IvrActionType | null;
    update: (newTypeId: IvrActionType) => void;
    isInSubmenu: boolean;
} & BaseProps;

const NONE_MESSAGE = defineMessage({id: "ivr.action.NONE", defaultMessage: "None"});

const IVR_ACTION_TYPE_MESSAGES = defineMessages<IvrActionType>({
    BACK_TO_GREETING: {id: "ivr.action.BACK_TO_GREETING", defaultMessage: "Back to welcome"},
    BACK_TO_MENU: {id: "ivr.action.BACK_TO_MENU", defaultMessage: "Back to menu"},
    BACK_TO_PREVIOUS_MENU: {id: "ivr.action.BACK_TO_PREVIOUS_MENU", defaultMessage: "Back to previous menu"},
    BACK_TO_TOP_MENU: {id: "ivr.action.BACK_TO_TOP_MENU", defaultMessage: "Back to top menu"},
    DIAL_NUMBER: {id: "ivr.action.DIAL_NUMBER", defaultMessage: "Dial user"},
    DISCONNECT: {id: "ivr.action.DISCONNECT", defaultMessage: "Disconnect"},
    FORWARD: {id: "ivr.action.FORWARD", defaultMessage: "Switch to"},
    FORWARD_TO_IVR: {id: "ivr.action.FORWARD_TO_IVR", defaultMessage: "Switch to Auto Attendant"},
    PLAY_CONNECTION_PROMPT: {id: "ivr.action.PLAY_CONNECTION_PROMPT", defaultMessage: "Play connection prompt"},
    SELECT_NUMBER: {id: "ivr.action.SELECT_NUMBER", defaultMessage: "Connect to ext. number"},
    SUBMENU: {id: "ivr.action.SUBMENU", defaultMessage: "Switch to submenu"},
});

// Button label messages
const IVR_BUTTON_LABEL_MESSAGES = defineMessages<number>({
    0: {id: "ivr.buttons.0", defaultMessage: "0"},
    1: {id: "ivr.buttons.1", defaultMessage: "1"},
    2: {id: "ivr.buttons.2", defaultMessage: "2"},
    3: {id: "ivr.buttons.3", defaultMessage: "3"},
    4: {id: "ivr.buttons.4", defaultMessage: "4"},
    5: {id: "ivr.buttons.5", defaultMessage: "5"},
    6: {id: "ivr.buttons.6", defaultMessage: "6"},
    7: {id: "ivr.buttons.7", defaultMessage: "7"},
    8: {id: "ivr.buttons.8", defaultMessage: "8"},
    9: {id: "ivr.buttons.9", defaultMessage: "9"},
    10: {id: "ivr.buttons.10", defaultMessage: "Default"},
    11: {id: "ivr.buttons.11", defaultMessage: "*"},
    12: {id: "ivr.buttons.12", defaultMessage: "#"},
});

const ButtonTypeSelector = ({currentType, update, isInSubmenu, disabled}: ButtonTypeSelectorProps) => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (currentType) {
        case IvrActionType.ForwardToIvr:
            currentType = IvrActionType.Forward;
            break;
        case IvrActionType.BackToGreeting:
            if (isInSubmenu) {
                currentType = undefined;
            }
            break;
    }

    return (
        <Select
            value={currentType ?? "NONE"}
            onChange={(e) => update(e.target.value != "NONE" ? (e.target.value as IvrActionType) : null)}
            disabled={disabled}
        >
            <MenuItem key={"not-set"} value={"NONE"}>
                <FormattedMessage {...NONE_MESSAGE} />
            </MenuItem>
            {Object.values(IvrActionType)
                .filter(
                    (type) =>
                        type != IvrActionType.ForwardToIvr &&
                        (isInSubmenu ? type != IvrActionType.BackToGreeting : true),
                )
                .map((type) => (
                    <MenuItem key={type} value={type}>
                        <FormattedMessage {...IVR_ACTION_TYPE_MESSAGES[type]} />
                    </MenuItem>
                ))}
        </Select>
    );
};

type ButtonTargetSelectorProps = {
    buttonType: IvrActionType;
    targets: Target[];
    currentNumberIdTarget?: number;
    currentExternalTarget?: string;
    updateTarget: (newTarget: {number?: number; external: string | undefined}) => void;
    index: number;
    errors?: ErrorWithContext[];
} & BaseProps;

const ButtonTargetSelector = ({
    buttonType,
    targets,
    currentNumberIdTarget,
    currentExternalTarget,
    updateTarget,
    disabled,
    index,
    errors,
}: ButtonTargetSelectorProps) => {
    if (buttonType === IvrActionType.SelectNumber) {
        return (
            <TextField
                key={`external-${buttonType}-${index}`}
                value={currentExternalTarget}
                onChange={(event) => updateTarget({number: undefined, external: event.target.value})}
                disabled={disabled}
                error={errors?.length ? true : false}
                helperText={<MultipleLineError errors={errors} />}
                slotProps={{
                    // This fixes an issue where the text input is 1 character wide in some cases
                    htmlInput: {
                        style: {width: "auto"},
                    },
                }}
            />
        );
    }

    if (buttonType === IvrActionType.Forward || buttonType === IvrActionType.ForwardToIvr) {
        return (
            <TargetSelector
                disabled={disabled}
                required
                autoSelectFirst
                targets={targets}
                selected={currentNumberIdTarget}
                handleChange={(newTarget) => {
                    updateTarget({number: newTarget, external: undefined});
                }}
                errors={errors}
            />
        );
    }

    return null;
};

type ButtonToSubmenuProps = {
    /** Callback to go deeper in the menu */
    goDeeper: (nextButton: number) => void;

    /** Index of the row in the table */
    index: number;

    /** Getter function to check whether the submenu (or its children) have errors */
    submenuHasError: (nextButton: number) => boolean;
} & BaseProps;

const ButtonToSubmenu = ({goDeeper, index, submenuHasError, disabled}: ButtonToSubmenuProps) => (
    <Grid container spacing={2} alignItems="center">
        <Grid>
            <FormattedButton
                message={defineMessage({id: "ivr.edit_submenu", defaultMessage: "Edit submenu"})}
                size="small"
                variant="contained"
                disabled={disabled}
                onClick={(_) => goDeeper(index)}
            />
        </Grid>
        {submenuHasError(index) && (
            <FormattedTooltip
                title={defineMessage({
                    id: "ivr.tooltips.submenu_has_error",
                    defaultMessage: "Error(s) occurred in this submenu or in its children",
                })}
                placement="bottom"
            >
                <ThemedFontAwesomeIcon icon="exclamation-triangle" color="error" size="2x" />
            </FormattedTooltip>
        )}
    </Grid>
);

type ButtonTargetColumnProps = {
    buttonType: IvrActionType;
} & ButtonToSubmenuProps &
    ButtonTargetSelectorProps;

const ButtonTargetColumn = ({buttonType, ...props}: ButtonTargetColumnProps) => {
    if (buttonType === IvrActionType.Submenu) {
        return <ButtonToSubmenu {...props} />;
    }
    return <ButtonTargetSelector buttonType={buttonType} {...props} />;
};

type ButtonConnectionPromptProps = {
    value: UpdateConnectionPrompt;
    update: (connectionPrompt: UpdateConnectionPrompt) => void;
    domainId: number;
    openMediaPlayerDialog: (mediaDownloadToken: MediaDownloadToken) => void;
    errors?: ErrorWithContext[];
} & BaseProps;

const ButtonConnectionPrompt = ({
    value,
    update,
    disabled,
    openMediaPlayerDialog,
    domainId,
    errors,
}: ButtonConnectionPromptProps) => {
    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <DomainMediaSelector
                domainId={domainId}
                value={value.id ?? value.file}
                onChange={(newValue) => {
                    if (newValue === undefined) return;

                    if (newValue instanceof File) {
                        update({
                            id: undefined,
                            file: newValue,
                            description: removeFileExtension(newValue.name),
                            downloadToken: undefined,
                            unset: false,
                        });
                    } else {
                        update({
                            id: newValue.id,
                            description: newValue.description,
                            file: undefined,
                            downloadToken: undefined,
                            unset: false,
                        });
                    }
                }}
                errors={errors}
            />
            {value.downloadToken && (
                <IconButton
                    size="medium"
                    onClick={() => openMediaPlayerDialog(value.downloadToken)}
                    disabled={disabled}
                >
                    <ThemedFontAwesomeIcon icon="play" color="primary" />
                </IconButton>
            )}
            {!value.unset && (value.id ?? value.file) && (
                <IconButton
                    size="medium"
                    onClick={() => {
                        update({
                            id: undefined,
                            file: undefined,
                            description: undefined,
                            downloadToken: undefined,
                            unset: true,
                        });
                    }}
                    disabled={disabled}
                >
                    <ThemedFontAwesomeIcon icon="times" color="primary" />
                </IconButton>
            )}
        </Stack>
    );
};

type Props = {
    /** The button for the row */
    button: UpdateButton;

    /** The targets that can be selected */
    targets: Target[];

    /** Callback to update the button */
    update: (newButton: DeepPartial<UpdateButton>) => void;

    /** Callback to open the media player dialog */
    openMediaPlayerDialog: (mediaDownloadToken: MediaDownloadToken) => void;

    /** Whether the button belongs to a submenu */
    isInSubmenu: boolean;

    domainId: number;

    targetErrors?: ErrorWithContext[];
    connectionPromptErrors: ErrorWithContext[];
} & BaseProps &
    ButtonToSubmenuProps;

/**
 * Row component for the ivr action table.
 * */
const ActionRow = ({
    index,
    button,
    targets,
    disabled,
    isInSubmenu,
    openMediaPlayerDialog,
    submenuHasError,
    update,
    goDeeper,
    domainId,
    targetErrors,
    connectionPromptErrors,
}: Props) => {
    return (
        <TableRow>
            <TableCell>
                <FormattedMessage
                    {...(IVR_BUTTON_LABEL_MESSAGES[index] ?? {id: "ivr.buttons.unknown", defaultMessage: "Button"})}
                />
            </TableCell>
            <TableCell>
                <ButtonTypeSelector
                    currentType={button.actionType}
                    update={(newType) => update({actionType: newType})}
                    isInSubmenu={isInSubmenu}
                    disabled={disabled}
                />
            </TableCell>
            <TableCell>
                <ButtonTargetColumn
                    buttonType={button.actionType}
                    index={index}
                    targets={targets}
                    submenuHasError={submenuHasError}
                    currentNumberIdTarget={button.targetNumberId}
                    currentExternalTarget={button.targetE164}
                    updateTarget={(newTarget) =>
                        update({targetNumberId: newTarget.number, targetE164: newTarget.external})
                    }
                    goDeeper={goDeeper}
                    disabled={disabled}
                    errors={targetErrors}
                />
            </TableCell>
            <TableCell>
                <ButtonConnectionPrompt
                    value={button.connectionPrompt}
                    domainId={domainId}
                    update={(connectionPrompt) => update({connectionPrompt})}
                    disabled={disabled}
                    errors={connectionPromptErrors}
                    openMediaPlayerDialog={openMediaPlayerDialog}
                />
            </TableCell>
        </TableRow>
    );
};

export default ActionRow;
